/**
 * Exponential backoff with full jitter for reconnect (design 8.1 / FR-13.3).
 *
 * On a drop the client reconnects, but a tight loop would hammer the hub and the BFF; a thundering
 * herd of browsers would be worse. So the delay doubles each attempt from a base to a cap, with
 * FULL JITTER (a uniform random point in `[0, delay]`) to spread reconnects out. On SSE the base is
 * the hub's advertised `retry` (3000 ms, design 8.1 "SSE"); on WebSocket a smaller base reconnects
 * faster. A successful connection resets the attempt count.
 *
 * The policy is a pure value; the client owns the attempt counter and the timer. `random` is
 * injectable so the delay is deterministic in a test.
 */

export interface BackoffPolicy {
  /** The delay for the first retry, before doubling, in ms. */
  readonly baseMs: number;
  /** The ceiling the doubled delay is clamped to, in ms. */
  readonly capMs: number;
}

/** WebSocket reconnects quickly; the hub can take many sockets. */
export const WEBSOCKET_BACKOFF: BackoffPolicy = { baseMs: 500, capMs: 30_000 };
/** SSE honours the hub's advertised retry of 3 s as the base (design 8.1). */
export const SSE_BACKOFF: BackoffPolicy = { baseMs: 3000, capMs: 30_000 };

/**
 * The delay before the given reconnect attempt (0-based: attempt 0 is the first retry after a
 * drop). The uncapped delay is `base * 2^attempt`, clamped to the cap, then full-jittered to a
 * uniform point in `[0, capped]`. Returns an integer number of ms.
 */
export function backoffDelay(
  policy: BackoffPolicy,
  attempt: number,
  random: () => number = Math.random,
): number {
  const safeAttempt = Math.max(0, Math.floor(attempt));
  // Clamp the exponent so `2 ** attempt` cannot overflow to Infinity on a very long outage.
  const exponent = Math.min(safeAttempt, 30);
  const uncapped = policy.baseMs * 2 ** exponent;
  const capped = Math.min(uncapped, policy.capMs);
  return Math.floor(random() * capped);
}
